import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { PaginatedResponse } from "../../../../core/http/paginated-response.type";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import {
  DropdownOption,
  DropdownQuestion,
  dropdownOptions,
} from "../../../../shared/components/dynamic-form/question-dropdown";
import { TextAreaQuestion } from "../../../../shared/components/dynamic-form/question-textarea";
import { TextboxQuestion } from "../../../../shared/components/dynamic-form/question-textbox";
import { pattern } from "../../../../shared/constants/patterns";
import { DEFAULT_QUERY_PARAMS } from "../../../../shared/constants/services";
import { UserInfo } from "../../../../shared/types/user-info";
import { setFilterCondition } from "../../../../shared/utils/helpers/common.helper";
import { CompanyService } from "../../companies/company.service";
import { Company } from "../../companies/company.type";
import { PartnerService } from "../../partners/partner.service";
import { Partner } from "../../partners/partner.type";
import { UserService } from "../../users/user.service";
import { User } from "../../users/user.type";
import { createStateField } from "../location";
import { LocationService } from "../location.service";
import { Country, Location } from "../location.type";
@Injectable()
export class CreateLocationService {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private COUNTRY_PAGE_SIZE = 500;
  constructor(
    private companyService: CompanyService,
    private userService: UserService,
    private partnerService: PartnerService,
    private locationService: LocationService,
  ) {}

  getCountries(query?: { name?: string }): Observable<DropdownOption[]> {
    const pagination = { ...DEFAULT_QUERY_PARAMS.pagination, pageSize: this.COUNTRY_PAGE_SIZE };
    return this.locationService
      .getCountries({ where: setFilterCondition(query, "$cont"), pagination })
      .pipe(
        map((r: PaginatedResponse<Country>) =>
          r.items.map((c: Country) => ({ value: c.id, label: c.name, iso: c.iso })),
        ),
      );
  }

  getPartners(query?: { name: string }): Observable<DropdownOption[]> {
    return this.partnerService
      .getPartners({ where: setFilterCondition(query, "$cont") })
      .pipe(map((r: PaginatedResponse<Partner>) => r.items.map((c: Partner) => ({ value: c.id, label: c.name }))));
  }

  getCompanies(query?: { partnerId: string; name?: string }): Observable<DropdownOption[]> {
    return this.companyService
      .getCompanies({ where: setFilterCondition(query, "$cont") })
      .pipe(map((r: PaginatedResponse<Company>) => r.items.map((c: Company) => ({ value: c.id, label: c.name }))));
  }

  getUsers(currentUser: UserInfo, query?: { companyId: string; value?: string }): Observable<DropdownOption[]> {
    if (query?.value) {
      // As user search query should contemplate user full name and
      // search input allows more than one word we split input content
      // and add the filter condition for each value and property
      const values = query.value.split(" ");
      const where = [];
      values.forEach((value) => {
        where.push(
          {
            ...setFilterCondition(
              { ...(!currentUser.isCompanyOrViewAsCompany() ? { companyId: query.companyId } : {}), firstName: value },
              "$cont",
            ),
          },
          {
            ...setFilterCondition(
              { ...(!currentUser.isCompanyOrViewAsCompany() ? { companyId: query.companyId } : {}), lastName: value },
              "$cont",
            ),
          },
          {
            ...setFilterCondition(
              { ...(!currentUser.isCompanyOrViewAsCompany() ? { companyId: query.companyId } : {}), email: value },
              "$cont",
            ),
          },
        );
      });
      return this.userService
        .getUsers({ where })
        .pipe(
          map((r: PaginatedResponse<User>) => r.items.map((c: User) => ({ value: c.id, label: this.getUserName(c) }))),
        );
    }

    return this.userService
      .getUsers({ where: setFilterCondition(query, "$cont") })
      .pipe(
        map((r: PaginatedResponse<User>) => r.items.map((u: User) => ({ value: u.id, label: this.getUserName(u) }))),
      );
  }

  getUserName(user: User): string {
    return `${user?.firstName} ${user?.lastName}`;
  }

  generateQuestions(
    options: {
      location?: Location;
      companyValue?: string;
      partnerValue?: string;
      companyOptions?: DropdownOption[];
      companyEdit?: boolean;
      companySkip?: boolean;
      partnerSkip?: boolean;
      currentUser?: UserInfo;
      isChannelWorksValue?: boolean;
      isChannelWorksSkip?: boolean;
      specialInstructionsSkip?: boolean;
    } = {
      companyEdit: true,
    },
  ): Observable<QuestionBase<string | boolean>[]> {
    if (options.companyEdit === undefined) {
      options.companyEdit = true;
    }

    const questions: QuestionBase<string | boolean>[] = [
      new TextboxQuestion({
        key: "name",
        label: "Location Name",
        value: options.location?.name,
        required: true,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Location Name is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Location Name is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Location Name is <strong>too long</strong>",
          },
        ],
        order: 1,
      }),

      new TextAreaQuestion({
        key: "description",
        label: "Description",
        value: options.location?.description,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Description is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Description is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(500),
            message: "Description is <strong>too long</strong>",
          },
        ],
        order: 2,
      }),

      !options.specialInstructionsSkip &&
        new TextAreaQuestion({
          key: "specialInstructions",
          label: "Special Instructions",
          value: options.location?.specialInstructions,
          hide: !options.currentUser?.isGlobal(),
          errors: [
            {
              key: "pattern",
              validator: Validators.pattern(pattern.words),
              message: "Special Instructions is <strong>not valid</strong>",
            },
            {
              key: "minlength",
              validator: Validators.minLength(1),
              message: "Special Instructions is <strong>too short</strong>",
            },
            {
              key: "maxlength",
              validator: Validators.maxLength(10000),
              message: "Special Instructions is <strong>too long</strong>",
            },
          ],
          order: 3,
        }),

      new TextboxQuestion({
        key: "address1",
        label: "Address 1",
        value: options.location?.address1,
        required: true,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Address is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Address is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Address is <strong>too long</strong>",
          },
        ],
        order: 4,
      }),

      new TextboxQuestion({
        key: "address2",
        label: "Address 2",
        value: options.location?.address2,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Address is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Address is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Address is <strong>too long</strong>",
          },
        ],
        order: 5,
      }),

      new TextboxQuestion({
        key: "address3",
        label: "Address 3",
        value: options.location?.address3,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Address is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Address is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Address is <strong>too long</strong>",
          },
        ],
        order: 6,
      }),

      new DropdownQuestion({
        key: "countryId",
        label: "Country",
        value: options.location?.countryId,
        currentOption: {
          label: options.location?.country?.name,
          value: options.location?.country?.id,
        },
        asyncOptions: this.getCountries(),
        sortOptions: false,
        search: {
          filter: {
            callback: ({ value: name }) => this.getCountries({ name }),
          },
        },
        required: true,
        order: 7,
      }),

      createStateField(options.location?.countryId, options.location?.state),

      new TextboxQuestion({
        key: "city",
        label: "City",
        value: options.location?.city,
        required: true,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "City is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "City is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "City is <strong>too long</strong>",
          },
        ],
        order: 9,
      }),

      new TextboxQuestion({
        key: "zip",
        label: "Zip",
        value: options.location?.zip,
        required: true,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Zip is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Zip is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Zip is <strong>too long</strong>",
          },
        ],
        order: 10,
      }),

      new TextboxQuestion({
        key: "phone",
        label: "Phone",
        value: options.location?.phone,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Phone is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Phone is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Phone is <strong>too long</strong>",
          },
        ],
        order: 11,
      }),

      !options?.partnerSkip &&
        new DropdownQuestion({
          value: options?.partnerValue,
          key: "partnerId",
          label: "Partner",
          currentOption: {
            label: options.location?.company?.partner?.name,
            value: options.location?.company?.partner?.id,
          },
          disabled: !options?.companyEdit,
          asyncOptions: this.getPartners(),
          search: {
            filter: {
              callback: ({ value: name }) => this.getPartners({ name }),
            },
          },
          required: true,
          order: 11,
        }),

      !options?.companySkip &&
        new DropdownQuestion({
          value: options?.companyValue,
          key: "companyId",
          label: "Company",
          currentOption: {
            label: options.location?.company?.name,
            value: options.location?.company?.id,
          },
          disabled: !options?.companyEdit,
          options: options?.companyOptions,
          ignoreInSubmit: options.currentUser.isCompany(),
          asyncOptions:
            options?.partnerValue &&
            this.getCompanies({
              partnerId: options?.partnerValue,
            }),
          dependsOn: {
            key: "partnerId",
            callback: !options?.companyEdit
              ? undefined
              : ({ parentValue: partnerId }) => this.getCompanies({ partnerId }),
          },
          search: {
            filter: {
              parentKey: "partnerId",
              callback: !options?.companyEdit
                ? undefined
                : ({ parentValue: partnerId, value: name }) => this.getCompanies({ partnerId, name }),
            },
          },
          required: true,
          order: 12,
        }),

      new DropdownQuestion({
        key: "primaryUserId",
        label: "Primary User",
        value: options.location?.primaryUserId,
        currentOption: {
          label: this.getUserName(options.location?.primaryUser),
          value: options.location?.primaryUser?.id,
        },
        asyncOptions:
          options?.companyValue &&
          this.getUsers(options.currentUser, {
            companyId: options.companyValue,
          }),
        dependsOn: {
          key: "companyId",
          callback: ({ parentValue: companyId }) =>
            !options?.companyValue &&
            this.getUsers(options.currentUser, { ...(!options.currentUser.isCompany() && { companyId: companyId }) }),
        },
        search: {
          filter: {
            parentKey: "companyId",
            callback: ({ parentValue: companyId, value }) =>
              this.getUsers(options.currentUser, {
                ...(!options.currentUser.isCompany() && { companyId: companyId || options.companyValue }),
                value,
              }),
          },
        },
        order: 14,
      }),

      !options?.isChannelWorksSkip &&
        new DropdownQuestion({
          key: "isChannelWorks",
          value: options.location?.isChannelWorks ?? options.isChannelWorksValue,
          options: dropdownOptions.yesNo,
          label: "Staff Managed",
          required: true,
          order: 14,
        }),
    ];

    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }
}
